import { types, mesgProcess } from '@dinosband/bv-main-client'
import { ChatRoom, ChatRoomType, JobOffer, Profile, User } from '@dinosband/bv-main-schema/dist/types'
import { Message, QueryFilter } from '@dinosband/bv-messenger-schema'
import type { Store as MainStore } from '@dinosband/bv-main-client'
import { mainStore } from '@dinosband/bv-utils'
import { EMPTY_MESG_FILTER, _isFiltered, _queryFilter, chatRoomQueryOption } from '@dinosband/bv-data-center'

import { ChatRoomState } from '../..'
import { ChatRoomStore } from './'
import { isPrivateChatRoom, PRIVATE_CHAT_ROOM_TYPES } from '@dinosband/bv-main-schema/dist/chat-room'

let contextId = 0

export interface ProcessAndState {
  processManager: mesgProcess.ProcessManager
  state: types.ProcessState
}

const _guestQueryFilter: QueryFilter = {
  ...EMPTY_MESG_FILTER,
  excludeFinished: false,
  excludeDeleted: true,
}

export class ChatRoomContext {

  static temporary (type: ChatRoomType, userProfileId: number, rootMessage: Message, rootChannelId?: number, jobOffer?: JobOffer, others?: Profile[]) {
    const context = new ChatRoomContext(mainStore, 0)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const context_ = context as any
    context_._chatRoomType = type
    context_._userProfileId = userProfileId
    context_._rootMessage = rootMessage
    context_._rootChannelId = rootChannelId
    context_._jobOffer = jobOffer
    context_._others = others
    if (rootMessage) {
      context_._tempProcessManager = new mesgProcess.ProcessManager()
      context_._tempProcessManager.initialize(mainStore, rootMessage.chatRoomId)
    }
    return context
  }

  id: number
  chatRoomId: number
  mainStore: MainStore
  store!: ChatRoomStore
  // mode?: 'guest'
  // guestMode = false
  failedMessage?: Message
  private _state: ChatRoomState | null

  /* for temporary */
  private _userProfileId?: number
  private _rootChannelId?: number
  private _jobOffer?: types.JobOffer
  private _others?: Profile[]
  private _tempProcessManager: mesgProcess.ProcessManager | undefined = undefined

  get processManager (): mesgProcess.ProcessManager | undefined {
    return this._tempProcessManager || this._state?.processManager
  }

  constructor (mainStore: MainStore, chatRoomId: number) {
    this.id = ++contextId
    this.mainStore = mainStore
    this.chatRoomId = chatRoomId
    this._state = null
  }

  /* should be initialized */
  init (store: ChatRoomStore) {
    this.store = store
    this._state = store.state
  }

  get process () {
    return this.processManager?.process
  }

  getState (processManager: mesgProcess.ProcessManager | undefined) {
    if (processManager) {
      if (this.jobOfferResponse) {
        const jrspState = this.jobOfferResponse?.state as types.JobOfferResponseState || 'Open'
        return processManager.getState(jrspState)
      } else {
        return processManager.getStartState()
      }
    } else {
      return undefined
    }
  }

  get processState () {
    const processManager = this.processManager
    return {
      processManager,
      state: this.getState(processManager),
    } as ProcessAndState
  }

  getStateName (ps: ProcessAndState): string {
    if (ps.processManager?.isProcessEmpty) { return '' }
    return ps.processManager?.getStateName(ps.state, this.jobOffer?.state as types.JobOfferState) || ''
  }

  getStateColor (ps: ProcessAndState): string {
    return ps.processManager?.getStateColor(ps.state, this.jobOffer?.state as types.JobOfferState) || ''
  }

  get userId (): number {
    return this.mainStore?.userId
  }

  get user (): User | null {
    return this.mainStore?.user
  }

  get userView () {
    return this.store.userView
  }

  get userProfile (): Profile | undefined {
    const profile = this._state?.userProfile
    if (profile) {
      return profile
    }
    const profileId = this._userProfileId
    if (!profileId) {
      return
    }
    return this.mainStore?.profiles.find((i) => i.id == profileId)
  }

  get userProfileId (): number | undefined {
    return this._userProfileId ?? this._state?.userProfile?.id
  }

  // get userProfile (): Profile | undefined {
  //   return this.participants.find((i) => i.id == this.userProfileId)
  // }

  get isChannelRoom () {
    if (!this.chatRoom) {
      return false
    }
    return ![...PRIVATE_CHAT_ROOM_TYPES, ChatRoomType.HELPDESK].includes(this.chatRoom.type)
  }

  get channel () {
    if (this.isPrivateChatroom) {
      return undefined
    }
    return this.rootChannel
  }

  get rootChannelId () {
    return this._rootChannelId ?? this.chatRoom?.channelId
  }

  _channelRequested = false
  get rootChannel () {
    let channel = this._state?.channel
    if (channel) {
      return channel
    }
    const channelId = this.rootChannelId
    if (!channelId) {
      return undefined
    }
    channel = this.mainStore.getJoinedChannelByChannelId(channelId)
    if (channel) {
      if (this._state) {
        this._state.channel = channel
      }
      return channel
    }
    if (!this._channelRequested) {
      this._channelRequested = true
      this.mainStore.readChannelDetail(channelId).then((result?: types.Channel) => {
        if (this._state && result) {
          this._state.channel = result
        }
        this._channelRequested = false
      })
    }
    return undefined
  }

  get categories () {
    return this.channel?.categories
  }

  get title () {
    return this.channel?.name
  }

  /*
  @AsyncComputed ()
  async channelDetail () {
    const channelId = this.chatRoom?.channelId
    if (!channelId) {
      return undefined
    }
    return await mainStore.readChannelDetail(channelId)
  }*/

  get participants (): Profile[] {
    if (this.isChannelRoom) {
      return this.channel?.profiles?.map((i) => i.profile) ?? []
    } else {
      return this.chatRoom?.participants?.map((i)=>i.profile) ?? []
    }
  }

  get others (): Profile[] {
    if (this._others) {
      return this._others
    }
    if (this._jobOffer) {
      return [this._jobOffer.client]
    }
    return this.participants?.filter((i) => i.id !== this.userProfileId) ?? []
  }

  get chatRoom (): ChatRoom | undefined {
    return this._state?.chatRoom
  }

  // private get chatRoomState (): ChatRoomState {
  //   // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  //   return this._state!
  // }
  private _chatRoomType?: ChatRoomType
  get chatRoomType () {
    return this.chatRoom?.type ?? this._chatRoomType ?? ChatRoomType.ONE_TO_ONE
  }

  get isTemporaryMode () {
    return this._chatRoomType != null
  }

  get isAvailableLikeChatroom () {
    return this.chatRoomType == ChatRoomType.GENERAL ||
           this.chatRoomType == ChatRoomType.JOB_OFFER
  }

  get isJobOfferChatroom () {
    if (!this.chatRoom) {
      return false
    }
    return this.mainStore.getChatRoomType(this.chatRoom) === types.ChatRoomType.JOB_OFFER
  }

  get isPrivateChatroom () {
    return isPrivateChatRoom(this.chatRoomType)
  }

  get othersReadMessageIds (): number[] {
    if (!this._state) {
      return []
    }
    return Object.values(this._state.usersReadMessageIds)
  }

  get rootMessageId () {
    const messageId = this._rootMessage?.id ?? this.chatRoom?.rootMessageId
    return messageId
  }

  private _rootMessage?: Message
  get rootMessage () {
    return this._rootMessage ?? this._state?.rootMessage
  }

  get rootChatRoom () {
    return this._state?.rootChatRoom
  }

  get isMyJobOffer () {
    return this._state?.isMyJobOffer
  }

  get jobOfferId (): number | undefined {
    return this.rootMessageId ?? undefined
  }

  get jobOffer (): types.JobOffer | undefined {
    return this._state?.jobOffer ?? this._jobOffer
  }

  get jobOfferResponse (): types.JobOfferResponse | undefined {
    return this._state?.jobOfferResponse ?? undefined
  }

  get jobOfferResponseProps (): types.JobOfferResponseProps | undefined {
    const props = this.jobOfferResponse?.props
    if (!props) {
      return
    }
    return JSON.parse(props)
  }

  get firstMessageId () {
    return this._state?.firstMessageId
  }

  get lastMessage () {
    return this._state?.lastMessage
  }

  get unreadCount () {
    return this._state?.unreadCount
  }

  get profile_Channel (): types.Profile_Channel | undefined {
    return this.mainStore.currentChannelAndProfile?.profile_channels[0]
  }

  get mesgFilterInfo () {
    return this._state?.filterInfo
  }

  private get filterEnabled () {
    return this._state?.filterInfo?.enableFilter ?? false
  }

  get channelFilter () {
    return this._state?.filterInfo?.channelFilter
  }

  get activeMesgFilter () {
    if (!this.filterEnabled) {
      return
    }
    return this.useChannelFilter ? this.channelFilter : this.customFilter
  }

  private get useChannelFilter () {
    return this._state?.filterInfo?.useChannelFilter
  }

  private get customFilter () {
    return this._state?.filterInfo?.customFilter
  }

  get isFiltered () {
    return _isFiltered(this._state?.filterInfo)
  }

  get queryFilter (): QueryFilter | undefined {
    if (!this.isJoined) {
      return _guestQueryFilter
    }
    const opt = chatRoomQueryOption(this.chatRoomType, this.isAdmin)
    return _queryFilter(this.chatRoomId, this._state?.filterInfo, opt)
  }

  get isOwned () {
    const channelId = this.channel?.id
    if (!channelId) { // ONE_TO_ONE은 channelIdrk 없음
      return false
    }
    return this.mainStore.amIOwnerOfChannel(channelId)
  }

  get isAdmin () {
    const channelId = this.channel?.id
    if (!channelId) { // ONE_TO_ONE은 channelIdrk 없음      return false
      return false
    }
    return this.mainStore.amIAdminOfChannel(channelId)
  }

  get isJoined () {
    const channelId = this.chatRoom?.channelId
    if (!this.isChannelRoom || !channelId) {
      return true
    }
    return this.mainStore.isJoinedChannelId(channelId)
  }

  get isWritable () {
    if (!this.isJoined) {
      return false
    }
    if (this.chatRoomType != ChatRoomType.NOTICE) {
      return true
    }
    return !!this.isOwned || this.isAdmin
  }

  get chatRoomProps () {
    return this._state?.props
  }

  get rootChatRoomProps () {
    return JSON.parse(this.rootChatRoom?.props ?? '{}') as types.ChatRoomProps
  }

  get allowGroupChat () {
    // return this.chatRoomId == 7846 || (this.chatRoomProps?.allowGroupChat ?? false)
    return this.chatRoomProps?.allowGroupChat ?? false
  }

  get allowExternalMessage () {
    return !this.chatRoomProps?.disallowExternalMessage
  }

  get channelProps () {
    const props = this.channel?.props
    return props ? JSON.parse(props) as types.ChannelProps : undefined
  }

  get rootChannelProps () {
    const props = this.rootChannel?.props
    return props ? JSON.parse(props) as types.ChannelProps : undefined
  }

  get useLocationFilter () {
    return this.channelProps?.useLocationFilter
  }

  get useTimeFilter () {
    return this.channelProps?.useTimeFilter
  }

}
